// ABOUTME: Resolves the labels the remote-update notice and conflict dialog show for a column or
// ABOUTME: a value, from the object schema of the table in the company default language.

type NameEntry = { languageIsoCode?: string | null; value?: string | null };
type SchemaContentType = {
    code: string;
    name?: NameEntry[] | null;
    multiLanguage?: boolean | null;
    optionalValues?: ({ code?: string | null; value?: NameEntry[] | null } | null)[] | null;
};
type SchemaGroup = {
    code: string;
    name?: NameEntry[] | null;
    contentTypes?: (SchemaContentType | null)[] | null;
};
export type RemoteUpdatesSchema = { contentTypeGroups?: (SchemaGroup | null)[] | null };

const pickName = (names: NameEntry[] | null | undefined, language: string, fallback: string) =>
    names?.find(n => n.languageIsoCode === language)?.value || fallback;

const findGroup = (schema: RemoteUpdatesSchema | undefined, contentTypeGroup: string) =>
    schema?.contentTypeGroups?.find(group => group?.code === contentTypeGroup) ?? undefined;

export const findContentType = (
    schema: RemoteUpdatesSchema | undefined,
    contentTypeGroup: string,
    contentType: string,
) =>
    findGroup(schema, contentTypeGroup)?.contentTypes?.find(ct => ct?.code === contentType) ??
    undefined;

export const getGroupLabel = (
    schema: RemoteUpdatesSchema | undefined,
    contentTypeGroup: string,
    language: string,
) => pickName(findGroup(schema, contentTypeGroup)?.name, language, contentTypeGroup);

export const getContentTypeLabel = (
    schema: RemoteUpdatesSchema | undefined,
    contentTypeGroup: string,
    contentType: string,
    language: string,
) => pickName(findContentType(schema, contentTypeGroup, contentType)?.name, language, contentType);

/**
 * Label of a column id, `${contentTypeGroup}-${contentType}`. Matched against every column of
 * the schema rather than split, since codes may themselves contain '-'.
 */
export const getColumnLabel = (
    schema: RemoteUpdatesSchema | undefined,
    columnId: string,
    language: string,
) => {
    const match = schema?.contentTypeGroups
        ?.flatMap(group => (group?.contentTypes ?? []).map(ct => ({ group, ct })))
        .find(({ group, ct }) => `${group?.code}-${ct?.code}` === columnId);
    return pickName(match?.ct?.name, language, columnId);
};

/** A stored value as shown to the user: select codes become their option label. */
export const getValueLabel = (
    contentType: SchemaContentType | undefined,
    value: unknown,
    language: string,
) => {
    const raw = value === null || value === undefined ? '' : String(value);
    const option = contentType?.optionalValues?.find(opt => opt?.code === raw);
    return option ? pickName(option.value, language, raw) : raw;
};
